// src/encounter/lib/questionnaire-groups.ts
// PROD-1234 divergence: HPI / ROS / PE / Risk are NOT separate fetches — they are sub-group
// SECTIONS within ONE Type-A `Questionnaire` (one fetch, one `QuestionnaireResponse`). The step
// nav selects which section to render. A section = the top-level groups whose `linkId` is prefixed
// with the step key (grounded in the authored template — the HPI groups are `hpi-onset` /
// `hpi-character` / `hpi-context`). The exact prefix convention tracks Template Studio's authored
// output; confirm against the real BFF template as ROS/PE/Risk sections are authored (PROD-1235..1239).
import type { PaStepKey } from './encounter-steps';
import {
  ENCOUNTER_STEP_KEYS,
  isBulkExempt,
  linkIdInStep,
  qrIsNormalChip,
  qrIsPosNegChip,
  type EncounterTemplateStepKey,
  type Questionnaire,
  type QItem,
  type QEnableWhen,
  type QuestionnaireResponse,
  type QRAnswerItem,
} from '@scripsteam/scrips-signal-ds';

/** A single answer's value — derived from the DS answer item (QAnswerValue isn't root-exported). */
type QAnswerValue = QRAnswerItem['value'];

/**
 * The steps whose content is a Questionnaire SECTION (vs the A/P canvas, Docs, or Wrap-up).
 *
 * Re-exports the DS's `ENCOUNTER_STEP_KEYS` rather than re-declaring the four literals. This used
 * to be PA's own hand-copy (PROD-1234); the DS module now publishes the rule both PA and PM
 * consumed independently — see `scrips-signal-ds/src/lib/encounter-step-keys.ts` for the full
 * history and why order (`hpi, risk, ros, pe`) is load-bearing nav order, not just membership.
 */
export const QUESTIONNAIRE_STEPS = ENCOUNTER_STEP_KEYS;
export type QuestionnaireStep = EncounterTemplateStepKey;

/** Is this step rendered from the shared Questionnaire (a section), rather than a bespoke surface?
 *  Takes the WIDENED `PaStepKey` (PROD-2040) so a PA-local step can be asked the question and
 *  correctly answer no — `allied-plan` is a bespoke surface, not a questionnaire section. */
export function isQuestionnaireStep(step: PaStepKey): step is QuestionnaireStep {
  return (QUESTIONNAIRE_STEPS as readonly string[]).includes(step);
}

/**
 * The section-membership convention (one source of truth): a top-level group belongs to a step
 * when its `linkId` equals the step key or is prefixed `${step}-`. Exported so non-DS call sites
 * (e.g. the FHIR-shaped chief-complaint save) scope by the SAME rule. `step` is a plain string so
 * it works on either the DS or FHIR questionnaire shape.
 *
 * The rule itself now lives in the DS (`linkIdInStep`) — this is a thin WIDENING wrapper, not a
 * reimplementation. `linkIdInStep`'s `step` parameter is the four-literal `EncounterTemplateStepKey`
 * union; PA's callers (see above) deliberately pass an arbitrary `string`, including FHIR-shaped
 * linkIds that are not one of the four template steps at all. Narrowing this signature to match the
 * DS would break those call sites, so the widening happens here, at the one place PA's usage
 * diverges from the DS's, via a runtime cast rather than by duplicating the DS's body.
 */
export function linkIdInSection(linkId: string, step: string): boolean {
  return linkIdInStep(linkId, step as EncounterTemplateStepKey);
}

function groupBelongsToStep(linkId: string, step: QuestionnaireStep): boolean {
  return linkIdInSection(linkId, step);
}

/**
 * Does the template actually use step-keyed sectioning? i.e. is ≥1 top-level group prefixed by a
 * step key (`hpi-`/`ros-`/`pe-`/`risk-`)? Real Template Studio output today does NOT — it authors
 * arbitrary author-named section groups (e.g. `sec-<guid>` "Health"), so step-sectioning is a
 * forward state (PROD-1235..1239). When absent, HPI owns the whole questionnaire (legacy behaviour,
 * = what `main` rendered) and the other steps degrade to the template-gate-relax notice — instead
 * of every step rendering empty, which would regress the live encounter.
 */
/**
 * Generic form of the step-sectioning check — is ≥1 top-level group prefixed by a step key?
 * Accepts any `{ linkId }[]` so non-DS call sites (the FHIR-shaped chief-complaint save) can reuse
 * the SAME rule as the renderer. (The CC bridge must mirror the render fallback: an unsectioned
 * template feeds the WHOLE questionnaire, else the HPI filter would yield nothing — Codex P2.)
 */
export function hasStepSectioning(items: ReadonlyArray<{ linkId?: string | null }>): boolean {
  return items.some((g) => !!g.linkId && QUESTIONNAIRE_STEPS.some((s) => linkIdInSection(g.linkId as string, s)));
}

function isStepSectioned(questionnaire: Questionnaire): boolean {
  return hasStepSectioning(questionnaire.item);
}

/**
 * The Questionnaire scoped to one step's section — its matching top-level groups only. Returns an
 * empty `item[]` when nothing is authored for the step (the caller degrades to the
 * template-gate-relax notice). Same object shape, so it drops straight into `QuestionnaireRenderer`.
 */
export function questionnaireForStep(questionnaire: Questionnaire, step: QuestionnaireStep): Questionnaire {
  // Not-yet-step-sectioned template (real TS output today): HPI owns the whole questionnaire;
  // the other steps render nothing (→ template-gate-relax notice). Un-regresses vs `main`.
  if (!isStepSectioned(questionnaire)) {
    return step === 'hpi' ? questionnaire : { ...questionnaire, item: [] };
  }
  return { ...questionnaire, item: questionnaire.item.filter((g) => groupBelongsToStep(g.linkId, step)) };
}

/** Does the Questionnaire have content to render for this step? */
export function stepHasContent(questionnaire: Questionnaire | undefined, step: QuestionnaireStep): boolean {
  if (!questionnaire) return false;
  // Unsectioned template → HPI shows the whole thing; other steps have no step-specific content.
  if (!isStepSectioned(questionnaire)) return step === 'hpi' && questionnaire.item.length > 0;
  return questionnaire.item.some((g) => groupBelongsToStep(g.linkId, step));
}

/**
 * The step nav, with questionnaire-driven steps that have NOTHING authored under them removed.
 *
 * A template that authors no PE section should not offer a PE tab that opens onto the
 * "configure in Template Studio" notice. But this is a CLINICAL nav: the codebase's own ruling
 * (see the gender-filter note below — "hiding clinically-needed sections is worse than showing an
 * inapplicable one") means every uncertain case must FAIL OPEN. The five open branches, in order:
 *
 *  1. NO QUESTIONNAIRE — absent, still loading, or the fetch errored (all three reach this view as
 *     `undefined`). We cannot know what is authored, so nothing is hidden.
 *  2. UNSECTIONED TEMPLATE — `hasStepSectioning` false. Today HPI owns the WHOLE questionnaire and
 *     ROS/PE/Risk render the template-gate-relax notice (see `questionnaireForStep`). That is
 *     current, shipped behaviour and this function preserves it EXACTLY: nothing is hidden, so an
 *     unsectioned template still shows every resolved tab. (Hiding ROS/PE/Risk here would change
 *     behaviour on every real Template Studio template served today — they are all unsectioned.)
 *  3. NON-QUESTIONNAIRE STEPS — `wrap-up`, `ap` and `docs` are never sourced from the questionnaire.
 *     Hiding them would remove function (sign-off, orders, documents), so they always pass.
 *  4. THE CURRENT STEP — never hidden, even if it becomes empty mid-session (e.g. the template
 *     reloads under the clinician). Yanking the active view out from under them is worse than an
 *     empty pane: the tab stays, its body shows the existing empty-state notice, and it drops out
 *     of the nav only once they navigate away.
 *  5. EMPTY RESULT — if the rule would leave zero tabs, every tab is shown.
 *
 * Pure and order-preserving; the input array is never mutated.
 */
export function visibleSteps<T extends PaStepKey>(
  steps: readonly T[],
  questionnaire: Questionnaire | undefined,
  currentStep: PaStepKey,
): T[] {
  // 1. Fail open on missing data — an absent / loading / errored questionnaire hides nothing.
  if (!questionnaire) return [...steps];
  // 2. Fail open on an unsectioned template — preserve today's behaviour exactly.
  if (!isStepSectioned(questionnaire)) return [...steps];
  const kept = steps.filter((step) => {
    // 3. Only questionnaire-driven steps are ever hidden.
    if (!isQuestionnaireStep(step)) return true;
    // 4. Never hide the step the clinician is standing on.
    if (step === currentStep) return true;
    return stepHasContent(questionnaire, step);
  });
  // 5. Never leave an empty nav.
  return kept.length > 0 ? kept : [...steps];
}

// ── Gender-scoped sections (PROD-1497) ──────────────────────────────────────────────────────
// Flutter filters template regions/findings by `genderCategory` (forMale/forFemale,
// common_encounter_component_base_tab.dart) — without it, a female patient sees Genitourinary
// (Male). INTERIM mechanism (BE-free): gendered top-level groups are authored with a `-male` /
// `-female` linkId suffix (the v2 template's `pe-genitourinary-female` / `-male`), mirroring the
// step-prefix convention. The durable form is `enableWhen` gating once QB persists it (PROD-804
// lane — QB still drops enableWhen on round-trip). Fail-open: an unknown/other sex ('X' or
// absent demographics) filters NOTHING — hiding clinically-needed sections is worse than
// showing an inapplicable one.
const GENDER_SUFFIX = /-(male|female)$/;

/** The questionnaire with opposite-sex items removed AT EVERY DEPTH. `sex` is the patient-header
 *  scalar ('M' | 'F'); anything else (or a non-suffixed item) passes through unfiltered.
 *
 *  PROD-2138 (2026-09-11): recursive. The Studio-authored "General Encounter" nests each body
 *  system one level under the step section (`ros-review-of-systems › ros-genitourinary-female`),
 *  and its findings carry the suffix too. The top-level-only filter let both GU variants through
 *  for every patient. A suffixed group drops with its whole subtree; a suffixed leaf drops alone. */
export function filterQuestionnaireByGender(
  questionnaire: Questionnaire,
  sex: string | undefined,
): Questionnaire {
  if (sex !== 'M' && sex !== 'F') return questionnaire;
  const keep = sex === 'M' ? '-male' : '-female';
  const prune = (items: QItem[]): QItem[] =>
    items
      .filter((it) => {
        const m = GENDER_SUFFIX.exec(it.linkId);
        return !m || it.linkId.endsWith(keep);
      })
      .map((it) => (it.item ? { ...it, item: prune(it.item) } : it));
  return { ...questionnaire, item: prune(questionnaire.item) };
}

// ── ROS symptom-chip presentation default (DS-033 adoption — THE STANDARD) ─────────────────
// The approved ROS exemplar (Signal DS 1.45.0, DS-033) renders each body-system finding as a
// tri-state SymptomChip (present / absent / not-assessed) in a wrapping chip row with a per-group
// "All Neg" bulk action — dispatched by QuestionnaireRenderer when a boolean item carries
// `itemControl='symptom-chip'`. The wire template does not author itemControl today (QB/PROD-804
// lane), so THIS is the seam: the app's section adapter defaults every ROS boolean finding to the
// symptom-chip control at render scope. An AUTHORED itemControl always wins (the default fills
// absence only), so the moment Template Studio serves the hint this default becomes a no-op.
// Rendering hint only — linkIds, answer types, and the emitted QuestionnaireResponse are
// unchanged (a chip answers valueBoolean true/false exactly like the checkbox it replaces).
// PE adopted 2026-07-22 (PROD-1588 interaction fix I2 — the section-level "All Normal" wave):
// PE boolean findings are the same tri-state normal/abnormal chips in Flutter
// (makeAllItemNegative runs on encounter ROS *and* PE), so the render-scope default now covers
// both. The copy differs per clinical context — ROS "All Neg" · PE "All Normal" — and stays
// host-owned (the QuestionnaireRenderer `allNegLabel` prop / the DS chrome's per-kind default).
const SYMPTOM_CHIP_STEPS: ReadonlyArray<QuestionnaireStep> = ['ros', 'pe'];
const SYMPTOM_CHIP_CONTROL = 'symptom-chip';

/** The section questionnaire with ROS/PE boolean findings defaulted to `itemControl='symptom-chip'`.
 *  Other steps pass through untouched (HPI/Risk booleans stay checkboxes). */
export function withSymptomChipDefaults(questionnaire: Questionnaire, step: QuestionnaireStep): Questionnaire {
  if (!SYMPTOM_CHIP_STEPS.includes(step)) return questionnaire;
  const map = (list: QItem[]): QItem[] =>
    list.map((it) => {
      if (it.item) return { ...it, item: map(it.item) };
      if (it.type === 'boolean' && it.itemControl == null) {
        return { ...it, itemControl: SYMPTOM_CHIP_CONTROL };
      }
      return it;
    });
  return { ...questionnaire, item: map(questionnaire.item) };
}

/** Every linkId under a set of items (recursively) — used to scope a response merge. */
export function collectLinkIds(items: QItem[]): Set<string> {
  const ids = new Set<string>();
  const walk = (list: QItem[]) => {
    for (const it of list) {
      ids.add(it.linkId);
      if (it.item) walk(it.item);
    }
  };
  walk(items);
  return ids;
}

/**
 * Merge a section's emitted response back into the whole-encounter response. We render a section
 * with the SCOPED questionnaire but the WHOLE response, so the renderer's emit echoes EVERY answer
 * it was seeded with (off-section included). Adopting that emit wholesale duplicates off-section
 * answers (edit ROS → HPI doubles). So: keep every answer OUTSIDE the section from `full`, and from
 * the emit take ONLY the section's answers. Net — editing one section never touches another, and
 * clearing a section field drops it (absent from the emit's section slice). One
 * `QuestionnaireResponse` spans the whole encounter (PROD-1234). [Codex/Samer gate fix, #111.]
 */
export function mergeSectionResponse(
  full: QuestionnaireResponse,
  sectionQuestionnaire: Questionnaire,
  emitted: QuestionnaireResponse,
): QuestionnaireResponse {
  const sectionIds = collectLinkIds(sectionQuestionnaire.item);
  const kept = full.answers.filter((a) => !sectionIds.has(a.linkId));
  const sectionAnswers = emitted.answers.filter((a) => sectionIds.has(a.linkId));
  return { ...full, answers: [...kept, ...sectionAnswers] };
}

// ── Section-level "All Neg" / "All Normal" (PROD-1588 interaction fix I2) ───────────────────
// Flutter truth: `makeAllItemNegative` (encounter ROS/PE) marks EVERY finding in the SECTION
// pertinent-negative/normal in one tap. The DS chrome exposes the seam (`onAllNeg` — rail
// end-slot link, per-kind copy "All Neg"/"All Normal"); these helpers give the host the flip.
// The chip-eligibility rule MIRRORS the DS's own per-group `markGroupNegative` exactly
// (enabled per enableWhen · `type='boolean'` · `itemControl='symptom-chip'` · not readOnly),
// so section-level and group-level bulk actions can never disagree about scope.
//
// PROD-2577: ROS and PE no longer use these — the DS renderer's `sectionRail` owns their rail,
// circles, All Neg and Clear (DS 2.2.0). The host chrome still offers them on a Risk step that
// authors chips of its own, so they follow the SAME semantics the DS applies: posneg → false,
// normal → true, offered only where a posneg finding exists.
//
// THE DS BULK GUARD (contract G7; signal-ds #412). The Risk step's All Neg used to OVERWRITE: every
// chip in scope was set, a recorded "present" flipped to "absent", and a suicide / self-harm item
// was answered "no" by a bulk tap. It now writes exactly what the DS renderer's own bulk path
// (`markGroupsNegative`) writes: only UNANSWERED findings, and never an item the DS marks bulk-exempt
// (`isBulkExempt` — a `cps-bulk|exempt` code or a risk label — inherited down the tree).

// enableWhen gate — a 1:1 MIRROR of the DS's `qrEnableWhenPass`/`qrItemEnabled`
// (QuestionnaireRenderer.tsx). The DS declares both in its QR .d.ts but does NOT re-export them
// from the package root (DS export gap, flagged upstream) — mirrored here verbatim so the
// section-level flip and the DS per-group flip can never disagree about which chips are enabled.
function enableWhenPass(cond: QEnableWhen, actual: QAnswerValue): boolean {
  if (cond.operator === 'exists') {
    const present = !(actual === undefined || actual === '' || (Array.isArray(actual) && actual.length === 0));
    return cond.answer === false ? !present : present;
  }
  if (actual === undefined || actual === '' || (Array.isArray(actual) && actual.length === 0)) return false;
  const expected = cond.answer;
  const eq = (a: QAnswerValue) => {
    if (Array.isArray(a)) return a.map(String).includes(String(expected));
    if (typeof expected === 'boolean') {
      return (typeof a === 'boolean' ? a : a === 'true' ? true : a === 'false' ? false : undefined) === expected;
    }
    return String(a) === String(expected);
  };
  switch (cond.operator) {
    case '=': return eq(actual);
    case '!=': return !eq(actual);
    case '>': return Number(actual) > Number(expected);
    case '<': return Number(actual) < Number(expected);
    case '>=': return Number(actual) >= Number(expected);
    case '<=': return Number(actual) <= Number(expected);
    default: return true;
  }
}

function itemEnabled(item: QItem, values: Record<string, QAnswerValue>): boolean {
  const ew = item.enableWhen;
  if (!ew || ew.length === 0) return true;
  const results = ew.map((c) => enableWhenPass(c, values[c.question]));
  return item.enableBehavior === 'any' ? results.some(Boolean) : results.every(Boolean);
}

/** The ENABLED, writable finding chips under `items`, split by the DS finding type
 *  (`qrIsPosNegChip` / `qrIsNormalChip` — the DS's own predicates, so the host and the renderer's
 *  section rail cannot disagree about which chip is which). `posneg` is the NEGATABLE set: it alone
 *  decides whether an All Neg is offered (production `hasPositiveNegativeTypeItem`). */
function findingChipIds(
  items: QItem[],
  response: QuestionnaireResponse,
): { posneg: string[]; normal: string[] } {
  const values: Record<string, QAnswerValue> = {};
  for (const a of response.answers) values[a.linkId] = a.value;
  const posneg: string[] = [];
  const normal: string[] = [];
  const walk = (it: QItem) => {
    if (!itemEnabled(it, values)) return;
    if (it.type === 'group') (it.item ?? []).forEach(walk);
    else if (it.readOnly) return;
    else if (qrIsPosNegChip(it)) posneg.push(it.linkId);
    else if (qrIsNormalChip(it)) normal.push(it.linkId);
  };
  items.forEach(walk);
  return { posneg, normal };
}

/** Every linkId a bulk action must never write: the DS's own `isBulkExempt`, INHERITED down the
 *  tree — a child of an exempt group ("Thoughts in the last month?" under "Suicide risk assessment")
 *  names no risk itself and is exempt all the same. Mirrors the DS's `bulkExemptLinkIds`, which the
 *  package does not export. Exported (PROD-2743) so the structured note's blocks flag the SAME items:
 *  one exemption rule for every bulk action. */
export function bulkExemptIds(items: readonly QItem[], inherited = false, out = new Set<string>()): Set<string> {
  for (const it of items) {
    const exempt = inherited || isBulkExempt(it);
    if (exempt && it.linkId) out.add(it.linkId);
    if (it.item?.length) bulkExemptIds(it.item, exempt, out);
  }
  return out;
}

/** The whole-encounter response with the DS bulk All Neg applied to `targets`: every UNANSWERED,
 *  non-exempt posneg finding → `false` (asserted absent) and every UNANSWERED, non-exempt normal
 *  finding → `true` (asserted normal) — the DS renderer's semantics (DS 2.2.0 `QItem.findingType`;
 *  signal-ds #412). A recorded answer is never changed and a bulk-exempt item is never written. A
 *  no-op when there is no posneg finding (a normal-only scope offers no All Neg at all) or nothing
 *  left to fill. Rides the SAME dispatch a single chip uses — an emitted response merged via
 *  `mergeSectionResponse` — so off-section answers and the section's non-chip answers are untouched. */
function applyBulkNegative(
  full: QuestionnaireResponse,
  sectionQuestionnaire: Questionnaire,
  targets: { posneg: string[]; normal: string[] },
): QuestionnaireResponse {
  if (targets.posneg.length === 0) return full;
  // Exemption is read over the WHOLE section, so a group's exempt ancestor still counts when the
  // action is scoped to that one group.
  const exempt = bulkExemptIds(sectionQuestionnaire.item);
  const values: Record<string, QAnswerValue> = {};
  for (const a of full.answers) values[a.linkId] = a.value;
  const writable = (id: string) => values[id] === undefined && !exempt.has(id);
  const posneg = targets.posneg.filter(writable);
  const normal = targets.normal.filter(writable);
  if (posneg.length === 0 && normal.length === 0) return full;
  const emitted: QuestionnaireResponse = {
    ...full,
    answers: [
      ...full.answers,
      ...posneg.map((linkId) => ({ linkId, value: false as const })),
      ...normal.map((linkId) => ({ linkId, value: true as const })),
    ],
  };
  return mergeSectionResponse(full, sectionQuestionnaire, emitted);
}

/** linkIds of every NEGATABLE (posneg) finding currently enabled in the section — the gate for
 *  the section-level All Neg. `sectionQuestionnaire` must be the CHIP-CARRYING render
 *  questionnaire (post `withSymptomChipDefaults`). Empty ⇒ the host must not render the control
 *  (anti-fiction: no affordance that flips nothing). Normal findings are not listed: they never
 *  make a scope negatable on their own. */
export function sectionSymptomChipIds(
  sectionQuestionnaire: Questionnaire,
  response: QuestionnaireResponse,
): string[] {
  return findingChipIds(sectionQuestionnaire.item, response).posneg;
}

/** The section's All Neg — fills unanswered findings only; see `applyBulkNegative`. */
export function fillSectionNegative(
  full: QuestionnaireResponse,
  sectionQuestionnaire: Questionnaire,
): QuestionnaireResponse {
  return applyBulkNegative(full, sectionQuestionnaire, findingChipIds(sectionQuestionnaire.item, full));
}

/** The section's GROUP whose linkId matches, at any depth, or undefined. PE/ROS author one group
 *  per body region (General · Eyes · HENT · …), which is the unit Flutter's per-category actions
 *  act on. */
function findGroup(sectionQuestionnaire: Questionnaire, groupLinkId: string): QItem | undefined {
  let found: QItem | undefined;
  const walk = (it: QItem) => {
    if (found) return;
    if (it.linkId === groupLinkId && it.type === 'group') {
      found = it;
      return;
    }
    if (it.type === 'group') (it.item ?? []).forEach(walk);
  };
  sectionQuestionnaire.item.forEach(walk);
  return found;
}

/** NEGATABLE (posneg) finding linkIds WITHIN one category group — same rule as
 *  `sectionSymptomChipIds`, scoped to the group's subtree so one category's action cannot touch a
 *  sibling category's answers. */
export function groupSymptomChipIds(
  sectionQuestionnaire: Questionnaire,
  response: QuestionnaireResponse,
  groupLinkId: string,
): string[] {
  const group = findGroup(sectionQuestionnaire, groupLinkId);
  return group ? findingChipIds(group.item ?? [], response).posneg : [];
}

/** One CATEGORY's All Neg — `applyBulkNegative` scoped to the group's subtree. */
export function fillGroupNegative(
  full: QuestionnaireResponse,
  sectionQuestionnaire: Questionnaire,
  groupLinkId: string,
): QuestionnaireResponse {
  const group = findGroup(sectionQuestionnaire, groupLinkId);
  if (!group) return full;
  return applyBulkNegative(full, sectionQuestionnaire, findingChipIds(group.item ?? [], full));
}

// ── ADDED NOTES narrative (PROD-1368) ────────────────────────────────────────────────────────
// The Encounter-2 HPI frame shows an "ADDED NOTES" free-text narrative below the section header
// (Flutter `notesSection`/`CommentWidget` — a read/edit-toggle editor with delete + expand). It is
// NOT a separate store: it binds to a free-text item authored INTO the section's Questionnaire,
// surfaced by the ADDED NOTES editor instead of as a structured field. The author marks that item
// with a `-narrative` linkId suffix (e.g. `hpi-cc-narrative`); we discover it by that convention so
// it generalises across sections (HPI/ROS/PE) as each authors a narrative. Because the editor owns
// it, the item is STRIPPED from the questionnaire handed to QuestionnaireRenderer (else it would be
// double-rendered as a one-line string field). When no narrative item is authored the editor stays
// disabled (anti-fiction — no binding to a non-existent field).
const NARRATIVE_LINKID_SUFFIX = '-narrative';

/** The section's free-text narrative item — the first non-group leaf whose linkId ends `-narrative`
 *  (recursively). `undefined` when the section authors none (→ ADDED NOTES stays disabled). */
export function findNarrativeItem(items: QItem[]): QItem | undefined {
  for (const it of items) {
    if (it.type !== 'group' && it.linkId.endsWith(NARRATIVE_LINKID_SUFFIX)) return it;
    if (it.item) {
      const nested = findNarrativeItem(it.item);
      if (nested) return nested;
    }
  }
  return undefined;
}

/** A copy of the questionnaire with one item (by linkId) removed recursively — used to keep the
 *  narrative item out of QuestionnaireRenderer (it is edited via the ADDED NOTES editor). */
export function stripItem(questionnaire: Questionnaire, linkId: string): Questionnaire {
  const prune = (list: QItem[]): QItem[] =>
    list
      .filter((it) => it.linkId !== linkId)
      .map((it) => (it.item ? { ...it, item: prune(it.item) } : it));
  return { ...questionnaire, item: prune(questionnaire.item) };
}

/** True when a group holds nothing but the narrative leaf `linkId` (directly or nested). */
export function holdsOnlyNarrative(group: QItem, linkId: string): boolean {
  // Every child is the narrative itself or a group that, in turn, holds only the narrative. Any other
  // child — a question, or an authored EMPTY group — is content, so the group stays.
  const kids = group.item ?? [];
  return kids.length > 0 && kids.every((it) => it.linkId === linkId || (it.type === 'group' && holdsOnlyNarrative(it, linkId)));
}

/** `stripItem` for the section narrative, and the group it leaves EMPTY goes too. A step with no group
 *  of its own carries its narrative in a notes-only group (template converter, PROD-2791 P14); stripping
 *  the leaf alone left that group drawn as a bare heading with a Clear button (measured with frameV2 off,
 *  5 Oct). Only a group the narrative was the sole content of is dropped — an authored empty group stays. */
export function stripNarrative(questionnaire: Questionnaire, linkId: string): Questionnaire {
  const prune = (list: QItem[]): QItem[] =>
    list.flatMap((it) => {
      if (it.linkId === linkId) return [];
      if (!it.item) return [it];
      if (it.type === 'group' && holdsOnlyNarrative(it, linkId)) return [];
      return [{ ...it, item: prune(it.item) }];
    });
  return { ...questionnaire, item: prune(questionnaire.item) };
}

/** True when ANY answer inside a group's linkId scope carries a non-empty value — drives the
 *  section rail's per-group completion dot (DS wave-2 R3/B6 seam, adopted PROD-1588 wave 4 L1).
 *  Empty-string / empty-array values don't count (setAnswer drops them; a cleared field is not
 *  "complete"). */
export function groupHasAnswer(response: QuestionnaireResponse, group: QItem): boolean {
  const ids = collectLinkIds([group]);
  return response.answers.some((a) => {
    if (!ids.has(a.linkId)) return false;
    const v = a.value;
    return !(v === undefined || v === '' || (Array.isArray(v) && v.length === 0));
  });
}

/** Read a single linkId's answer as a string ('' when absent or non-string). */
export function answerString(response: QuestionnaireResponse, linkId: string): string {
  const v = response.answers.find((a) => a.linkId === linkId)?.value;
  return typeof v === 'string' ? v : '';
}

/** Upsert (or, on an empty value, drop) one linkId's answer, leaving every other answer untouched —
 *  the one-QuestionnaireResponse contract (PROD-1234). Dropping-on-empty mirrors how clearing a
 *  section field drops it in `mergeSectionResponse`, so an emptied narrative leaves no stray answer. */
export function setAnswer(
  response: QuestionnaireResponse,
  linkId: string,
  value: QAnswerValue,
): QuestionnaireResponse {
  const others = response.answers.filter((a) => a.linkId !== linkId);
  const isEmpty = value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
  return { ...response, answers: isEmpty ? others : [...others, { linkId, value }] };
}

/**
 * The groups the section's jump rail should list.
 *
 * ROS authors one top-level group per body system, so its rail is just `section.item` — 14 chips,
 * one per system. PE (and any Studio template that wraps its categories) authors ONE top-level
 * group, "Physical Exam", whose children ARE the categories: General · Eyes · HENT · … So reading
 * `section.item` there yields a single chip carrying the section's own name, which is the whole
 * section, not a shortcut to anywhere within it.
 *
 * Ahmed, 2026-09-14, seeing exactly that: "i didnt want Physical Exam(complete) … i wanted like
 * ROS, jump shortcut to each section of what we select."
 *
 * So: descend through a LONE wrapper group whose children are themselves groups, and stop as soon
 * as that is not the shape. A section that already lists its categories at the top level is
 * returned untouched, which is why ROS is unaffected. A wrapper whose children are QUESTIONS
 * (the flat demo fixture) is also returned untouched — descending there would list questions as
 * if they were categories.
 *
 * Only a LONE wrapper is descended through. Two or more top-level groups are already the category
 * list, and flattening them would drop the level the author actually chose.
 */
export function railGroups(sectionQuestionnaire: Questionnaire): QItem[] {
  let level = sectionQuestionnaire.item;
  // Bounded: a pathological template cannot spin this, and no real one nests this deep.
  for (let depth = 0; depth < 4; depth += 1) {
    if (level.length !== 1) return level;
    const only = level[0];
    if (only.type !== 'group') return level;
    const children = (only.item ?? []).filter((c) => c.type === 'group');
    // Every child must be a group — a mixed wrapper (some questions, some groups) is the
    // author's own level and is left alone rather than silently half-flattened.
    if (children.length === 0 || children.length !== (only.item ?? []).length) return level;
    level = children;
  }
  return level;
}

/** TRUE when the section's own groups ARE the top level — one group per body system, no wrapper.
 *  That is the shape the wire contract DECIDES for ROS ("ROS flattens on the write side", rule 1
 *  of encounter-template-wire-contract.md) and the shape the legacy Flutter adapter already
 *  emitted. The WRAPPED shape — one `ros-review-of-systems` group holding the systems as region
 *  sections — is what dev serves until the converted template is published, and the two need
 *  different layouts (once §25/§26 of the PA's encounter-design-overrides.css, deleted PA-Z
 *  2026-09-23; now the DS `EncounterStepPanel rosShape`). Derived from `railGroups` rather
 *  than a second detector, so the layout can never disagree with the rail about the shape.
 *  Identity, not length: `railGroups` returns the section's own `item` array by reference when it
 *  did not descend. */
export function isFlatGroupShape(sectionQuestionnaire: Questionnaire): boolean {
  return railGroups(sectionQuestionnaire) === sectionQuestionnaire.item;
}

/** A group anywhere in the section by linkId — the rail's jump target may be nested one level
 *  below the top (see `railGroups`), so a top-level-only lookup misses it. */
export function findGroupDeep(sectionQuestionnaire: Questionnaire, linkId: string): QItem | undefined {
  return findGroup(sectionQuestionnaire, linkId);
}

// ── Rail abbreviation (PROD-2351) ───────────────────────────────────────────────────────────
// `railAbbreviation` and `CPS_ABBREVIATION_SYSTEM` live in the Signal DS now
// (`lib/encounter-codings`, DS 2.2.0) — the renderer's section rail reads the same carrier, so the
// PA imports them rather than keeping a second copy (a shadow `check-ds-shadows` rejects). Same
// rule: matched by SYSTEM, `code` first, `display` as the fallback; the DS also returns
// `undefined` when both are empty, so a caller's `?? text` fallback still applies.
//
// `encounter-shell-view.tsx` (the section rail) and its tests import both names directly from
// `@scripsteam/scrips-signal-ds` now. This re-export stays so any consumer that still imports
// from `./questionnaire-groups` (the pre-2.2.0 pattern) keeps working unchanged — same reasoning
// as `QUESTIONNAIRE_STEPS = ENCOUNTER_STEP_KEYS` above.
export { CPS_ABBREVIATION_SYSTEM, railAbbreviation } from '@scripsteam/scrips-signal-ds';

// ── Risk step ownership (PROD-2354 → PROD-2577) ─────────────────────────────────────────────
// The Risk step used to have two owners: a bespoke Fall/Pain panel (EncounterMetaData) for the
// reserved `risk-assessment` group, stripped from the renderer here, and the renderer for any other
// `risk-` group. Ahmed's ruling (2026-09-24, PROD-2577; closes PROD-2354) retired the panel: the
// renderer draws the whole Risk section, and the page bridges the `fall-assessment` /
// `wong-baker-pain` groups' answers to EncounterMetaData (`encounter/risk/lib/risk-qr-bridge.ts`).
// Nothing is stripped, so `BESPOKE_RISK_GROUPS` and its three helpers are gone.
